import { useState } from 'react'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import {
  AlertCircleIcon,
  ChevronDownIcon,
  LayoutGridIcon,
  PackageIcon,
  ReceiptIcon,
  TicketIcon,
} from 'lucide-react'

import { ApiClientError } from '@ems/api-client/client'
import { Alert, AlertDescription, AlertTitle } from '@ems/ui/components/alert'
import { Button } from '@ems/ui/components/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@ems/ui/components/dropdown-menu'
import { PageHeader } from '@ems/ui/patterns/page-header'
import { formatDateTime } from '@ems/ui/lib/format'

import { NotFoundView } from '#admin/components/not-found-view.tsx'
import { ReadOnlyNotice } from '#admin/components/read-only-notice.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import {
  applicationActions,
  canAssignBooths,
  hasPricing,
  NO_APPLICATION_ACTIONS,
  waitingFor,
} from '#admin/features/exhibitor/api.ts'
import { AssignBoothsDialog } from '#admin/features/exhibitor/assign-booths-dialog.tsx'
import {
  ApplicationDialogs,
  type ApplicationDialog,
} from '#admin/features/exhibitor/application-dialogs.tsx'
import {
  BoothsSection,
  CompanySection,
  PricingSection,
  StatusSection,
} from '#admin/features/exhibitor/application-sections.tsx'
import {
  ApplicationActionItems,
  hasForwardAction,
} from '#admin/features/exhibitor/application-row-actions.tsx'
import { ApplicationSkeleton } from '#admin/features/exhibitor/exhibitor-skeletons.tsx'
import { FitOutSection } from '#admin/features/exhibitor/fit-out-section.tsx'
import { PassesSection } from '#admin/features/exhibitor/passes-section.tsx'
import { applicationQuery, passesQuery } from '#admin/features/exhibitor/queries.ts'
import { passRegistrations } from '#admin/features/exhibitor/stand-extras-api.ts'
import { manifestQuery } from '#admin/features/tenant/queries.ts'

/**
 * `/exhibition/exhibitors/$applicationId` — one stand booking, and every decision an operator makes on it.
 *
 * **The page offers a verb only when the application accepts it** (`applicationActions`, which
 * asks the same state machine the server uses). The server refuses each verb again, so this
 * page only stops an operator from clicking a button that would fail.
 */
export const Route = createFileRoute('/_authenticated/exhibition/exhibitors/$applicationId/')({
  pendingComponent: ApplicationSkeleton,
  loader: async ({ context, params }) => {
    try {
      await context.queryClient.ensureQueryData(applicationQuery(params.applicationId))
    } catch (error) {
      // An application that does not exist is "not here", not a failure. A module missing from
      // the plan is also a 404, and the ErrorView explains that one.
      if (error instanceof ApiClientError && error.status === 404 && !error.isMissingFeature) {
        throw notFound()
      }
      throw error
    }
  },
  notFoundComponent: NotFoundView,
  component: ApplicationPage,
})

function ApplicationPage() {
  const { applicationId: id } = Route.useParams()
  const { data: application } = useSuspenseQuery(applicationQuery(id))
  const { data: session } = useSuspenseQuery(sessionQuery())
  const { data: manifest } = useQuery(manifestQuery())
  const [dialog, setDialog] = useState<ApplicationDialog>(null)
  const [assigning, setAssigning] = useState(false)

  // A view-only role sees the application and none of its verbs. The server refuses them anyway.
  const canManage = can(session, 'exhibitor.manage')
  const actions = canManage ? applicationActions(application) : NO_APPLICATION_ACTIONS
  const forward = hasForwardAction(actions)
  // With no step forward, say what the application waits for. A view-only role has the notice.
  const hint = canManage && !forward ? waitingFor(application.status) : null
  // Passes and the fit-out belong to a stand that is booked: approved, invoiced or paid.
  const booked =
    application.status === 'approved' ||
    application.status === 'payment_pending' ||
    application.status === 'payment_completed'
  const hasPasses = application.status === 'approved' || application.status === 'payment_completed'
  const { data: passes } = useQuery({ ...passesQuery(id), enabled: hasPasses })
  const [badgeRegistration] = passRegistrations(passes?.items ?? [])

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={application.companyName}
        description={[
          application.code ?? 'Draft',
          application.contactName,
          application.contactEmail,
        ].join(' · ')}
        actions={
          <>
            {hint === null ? null : <p className="text-muted-foreground text-sm">{hint}</p>}
            {forward || actions.recreateInvoice || actions.cancel ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant={forward ? 'default' : 'outline'}>
                    Actions
                    <ChevronDownIcon />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <ApplicationActionItems actions={actions} open={setDialog} />
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
            {canManage &&
            (application.status === 'approved' || application.status === 'payment_completed') ? (
              <Button asChild variant="outline">
                <Link
                  to="/exhibition/service-orders/new"
                  search={{ applicationId: application.id }}
                >
                  <PackageIcon />
                  New service order
                </Link>
              </Button>
            ) : null}
            {badgeRegistration === undefined ? null : (
              <Button asChild variant="outline">
                <Link
                  to="/ticketing/registrations/$registrationId"
                  params={{ registrationId: badgeRegistration }}
                >
                  <TicketIcon />
                  View badges and registration
                </Link>
              </Button>
            )}
            {application.invoiceId === null ? null : (
              <Button asChild variant="outline">
                <Link
                  to="/billing/invoices/$invoiceId"
                  params={{ invoiceId: application.invoiceId }}
                >
                  <ReceiptIcon />
                  View invoice
                </Link>
              </Button>
            )}
          </>
        }
      />

      {canManage ? null : <ReadOnlyNotice area="this application" />}

      {application.status === 'rejected' ? (
        <Alert variant="destructive">
          <AlertCircleIcon />
          <AlertTitle>
            Rejected
            {application.rejectedAt === null ? '' : ` ${formatDateTime(application.rejectedAt)}`}
          </AlertTitle>
          <AlertDescription>
            {application.rejectionReason ?? 'No reason was recorded.'}
          </AlertDescription>
        </Alert>
      ) : null}

      {application.status === 'canceled' && application.canceledAt !== null ? (
        <Alert variant="destructive">
          <AlertCircleIcon />
          <AlertTitle>Canceled {formatDateTime(application.canceledAt)}</AlertTitle>
        </Alert>
      ) : null}

      <div className="grid gap-6 md:grid-cols-2">
        <StatusSection application={application} />
        <CompanySection application={application} />
      </div>

      <BoothsSection
        booths={application.booths ?? []}
        currency={application.currency}
        {...(canManage && canAssignBooths(application)
          ? {
              action: (
                <Button variant="outline" size="sm" onClick={() => setAssigning(true)}>
                  <LayoutGridIcon />
                  {(application.booths ?? []).length === 0 ? 'Assign stands' : 'Change stands'}
                </Button>
              ),
            }
          : {})}
      />
      {assigning ? (
        <AssignBoothsDialog application={application} onClose={() => setAssigning(false)} />
      ) : null}

      {hasPricing(application.status) ? <PricingSection applicationId={id} /> : null}

      {hasPasses ? (
        <PassesSection
          application={application}
          canManage={canManage}
          eventName={manifest?.tenant.name ?? ''}
        />
      ) : null}
      {booked ? <FitOutSection applicationId={id} canManage={canManage} /> : null}

      <ApplicationDialogs
        application={application}
        dialog={dialog}
        onClose={() => setDialog(null)}
        zone={manifest?.tenant.timezone ?? 'UTC'}
      />
    </div>
  )
}
